import type { WsEvent } from '../lib/ws-client';
import type { CanvasNode } from './CanvasView';

export type KnowledgeStatus = 'active' | 'superseded' | 'dismissed';
export type KnowledgeConflictStatus = 'open' | 'resolved' | 'dismissed';
export type KnowledgeResolution = 'overwrite' | 'narrow' | 'branch' | 'keep_old';

export interface CanvasKnowledgeItem {
  item_id: string;
  knowledge_id: string;
  session_id: string | null;
  kind: string;
  content: string;
  created_by: string | null;
  scope_kind: string;
  scope_node_id: string | null;
  source_kind: string;
  source_node_id: string | null;
  source_run_id: string | null;
  source_output_id: string | null;
  source_audit_start: number | null;
  source_audit_end: number | null;
  source_agent_id: string | null;
  source_field: string | null;
  source_index: number | null;
  status: KnowledgeStatus;
  superseded_by_item_id: string | null;
  resolved_conflict_id: string | null;
  created_at: number;
  updated_at: number;
}

export interface CanvasKnowledgeConflict {
  conflict_id: string;
  session_id: string;
  candidate_item_id: string;
  existing_item_id: string;
  status: KnowledgeConflictStatus;
  resolution: KnowledgeResolution | null;
  reason: string;
  created_at: number;
  resolved_at: number | null;
  resolved_by_user_id: string | null;
}

export interface CanvasKnowledgeRevision {
  revision_id: string;
  item_id: string;
  session_id: string | null;
  kind: string;
  content: string;
  status: KnowledgeStatus;
  editor_kind: string;
  editor_id: string | null;
  reason: string | null;
  conflict_id: string | null;
  created_at: number;
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function textValue(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function intValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function statusValue(value: unknown): KnowledgeStatus {
  return value === 'superseded' || value === 'dismissed' ? value : 'active';
}

function conflictStatusValue(value: unknown): KnowledgeConflictStatus {
  if (value === 'resolved' || value === 'dismissed') return value;
  return 'open';
}

function resolutionValue(value: unknown): KnowledgeResolution | null {
  if (value === 'overwrite' || value === 'narrow' || value === 'branch' || value === 'keep_old') return value;
  return null;
}

function eventWorkspaceMatches(evt: WsEvent, workspaceId?: string): boolean {
  return !workspaceId || evt.workspace_id === workspaceId;
}

function rowToKnowledgeItem(row: Record<string, unknown>): CanvasKnowledgeItem | null {
  const itemId = textValue(row.item_id) ?? textValue(row.knowledge_id);
  const content = textValue(row.content) ?? textValue(row.content_preview);
  const kind = textValue(row.kind);
  if (!itemId || !content || !kind) return null;
  const scope = objectValue(row.scope);
  const source = objectValue(row.source);
  return {
    item_id: itemId,
    knowledge_id: itemId,
    session_id: textValue(row.session_id),
    kind,
    content,
    created_by: textValue(row.created_by),
    scope_kind: textValue(row.scope_kind) ?? textValue(scope.kind) ?? 'session',
    scope_node_id: textValue(row.scope_node_id) ?? textValue(scope.node_id),
    source_kind: textValue(row.source_kind) ?? textValue(source.kind) ?? 'manual',
    source_node_id: textValue(row.source_node_id) ?? textValue(source.node_id),
    source_run_id: textValue(row.source_run_id) ?? textValue(source.run_id),
    source_output_id: textValue(row.source_output_id) ?? textValue(source.output_id),
    source_audit_start: intValue(row.source_audit_start) ?? intValue(source.audit_start),
    source_audit_end: intValue(row.source_audit_end) ?? intValue(source.audit_end),
    source_agent_id: textValue(row.source_agent_id) ?? textValue(source.agent_id),
    source_field: textValue(row.source_field) ?? textValue(source.field),
    source_index: intValue(row.source_index) ?? intValue(source.index),
    status: statusValue(row.status),
    superseded_by_item_id: textValue(row.superseded_by_item_id),
    resolved_conflict_id: textValue(row.resolved_conflict_id),
    created_at: intValue(row.created_at) ?? 0,
    updated_at: intValue(row.updated_at) ?? intValue(row.created_at) ?? 0,
  };
}

function rowToKnowledgeConflict(row: Record<string, unknown>): CanvasKnowledgeConflict | null {
  const conflictId = textValue(row.conflict_id);
  const candidate = textValue(row.candidate_item_id);
  const existing = textValue(row.existing_item_id);
  if (!conflictId || !candidate || !existing) return null;
  return {
    conflict_id: conflictId,
    session_id: textValue(row.session_id) ?? '',
    candidate_item_id: candidate,
    existing_item_id: existing,
    status: conflictStatusValue(row.status),
    resolution: resolutionValue(row.resolution),
    reason: textValue(row.reason) ?? 'knowledge conflict',
    created_at: intValue(row.created_at) ?? 0,
    resolved_at: intValue(row.resolved_at),
    resolved_by_user_id: textValue(row.resolved_by_user_id),
  };
}

function rowToKnowledgeRevision(row: Record<string, unknown>): CanvasKnowledgeRevision | null {
  const revisionId = textValue(row.revision_id);
  const itemId = textValue(row.item_id);
  const content = textValue(row.content);
  const kind = textValue(row.kind);
  if (!revisionId || !itemId || !content || !kind) return null;
  return {
    revision_id: revisionId,
    item_id: itemId,
    session_id: textValue(row.session_id),
    kind,
    content,
    status: statusValue(row.status),
    editor_kind: textValue(row.editor_kind) ?? 'system',
    editor_id: textValue(row.editor_id),
    reason: textValue(row.reason),
    conflict_id: textValue(row.conflict_id),
    created_at: intValue(row.created_at) ?? 0,
  };
}

export function eventsToKnowledgeItems(events: WsEvent[], workspaceId?: string): CanvasKnowledgeItem[] {
  const byId = new Map<string, CanvasKnowledgeItem>();
  const sorted = [...events].sort((a, b) => (a.audit_id ?? 0) - (b.audit_id ?? 0));
  for (const evt of sorted) {
    if (!eventWorkspaceMatches(evt, workspaceId)) continue;
    const payload = objectValue(evt.payload);
    if (evt.kind === 'workflow.snapshot') {
      const rows = Array.isArray(payload.knowledge_items) ? payload.knowledge_items : [];
      for (const row of rows) {
        const item = rowToKnowledgeItem(objectValue(row));
        if (item) byId.set(item.item_id, item);
      }
      continue;
    }
    if (evt.kind === 'knowledge.added') {
      const item = rowToKnowledgeItem(payload);
      if (item) byId.set(item.item_id, item);
      continue;
    }
    if (evt.kind === 'knowledge.conflict_resolved') {
      const candidateId = textValue(payload.candidate_item_id);
      const existingId = textValue(payload.existing_item_id);
      const candidateStatus = statusValue(payload.candidate_status);
      const existingStatus = statusValue(payload.existing_status);
      const conflictId = textValue(payload.conflict_id);
      const content = textValue(payload.content_preview);
      if (candidateId && byId.has(candidateId)) {
        const item = byId.get(candidateId)!;
        byId.set(candidateId, {
          ...item,
          status: candidateStatus,
          content: content ?? item.content,
          resolved_conflict_id: conflictId ?? item.resolved_conflict_id,
        });
      }
      if (existingId && byId.has(existingId)) {
        const item = byId.get(existingId)!;
        byId.set(existingId, {
          ...item,
          status: existingStatus,
          superseded_by_item_id: candidateId ?? item.superseded_by_item_id,
          resolved_conflict_id: conflictId ?? item.resolved_conflict_id,
        });
      }
    }
  }
  return [...byId.values()].sort((a, b) => a.created_at - b.created_at || a.item_id.localeCompare(b.item_id));
}

export function eventsToKnowledgeConflicts(events: WsEvent[], workspaceId?: string): CanvasKnowledgeConflict[] {
  const byId = new Map<string, CanvasKnowledgeConflict>();
  const sorted = [...events].sort((a, b) => (a.audit_id ?? 0) - (b.audit_id ?? 0));
  for (const evt of sorted) {
    if (!eventWorkspaceMatches(evt, workspaceId)) continue;
    const payload = objectValue(evt.payload);
    if (evt.kind === 'workflow.snapshot') {
      const rows = Array.isArray(payload.knowledge_conflicts) ? payload.knowledge_conflicts : [];
      for (const row of rows) {
        const conflict = rowToKnowledgeConflict(objectValue(row));
        if (conflict) byId.set(conflict.conflict_id, conflict);
      }
      continue;
    }
    if (evt.kind === 'knowledge.conflict_opened') {
      const conflict = rowToKnowledgeConflict(payload);
      if (conflict) byId.set(conflict.conflict_id, conflict);
      continue;
    }
    if (evt.kind === 'knowledge.conflict_resolved') {
      const conflictId = textValue(payload.conflict_id);
      if (!conflictId) continue;
      const current = byId.get(conflictId);
      byId.set(conflictId, {
        conflict_id: conflictId,
        session_id: textValue(payload.session_id) ?? current?.session_id ?? '',
        candidate_item_id: textValue(payload.candidate_item_id) ?? current?.candidate_item_id ?? '',
        existing_item_id: textValue(payload.existing_item_id) ?? current?.existing_item_id ?? '',
        status: 'resolved',
        resolution: resolutionValue(payload.resolution),
        reason: current?.reason ?? 'knowledge conflict resolved',
        created_at: current?.created_at ?? 0,
        resolved_at: intValue(evt.ts) ?? Date.now(),
        resolved_by_user_id: textValue(payload.resolved_by_user_id),
      });
    }
  }
  return [...byId.values()].sort((a, b) => a.created_at - b.created_at || a.conflict_id.localeCompare(b.conflict_id));
}

export function eventsToKnowledgeRevisions(events: WsEvent[], workspaceId?: string): CanvasKnowledgeRevision[] {
  const byId = new Map<string, CanvasKnowledgeRevision>();
  for (const evt of events) {
    if (!eventWorkspaceMatches(evt, workspaceId)) continue;
    if (evt.kind !== 'workflow.snapshot') continue;
    const payload = objectValue(evt.payload);
    const rows = Array.isArray(payload.knowledge_revisions) ? payload.knowledge_revisions : [];
    for (const row of rows) {
      const revision = rowToKnowledgeRevision(objectValue(row));
      if (revision) byId.set(revision.revision_id, revision);
    }
  }
  return [...byId.values()].sort((a, b) => a.created_at - b.created_at || a.revision_id.localeCompare(b.revision_id));
}

export function knowledgeRefId(ref: unknown): string | null {
  const record = objectValue(ref);
  if (textValue(record.kind) !== 'knowledge' && textValue(record.type) !== 'knowledge') return null;
  return textValue(record.knowledge_id) ?? textValue(record.ref_id);
}

export function knowledgeUsageCounts(nodes: CanvasNode[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const node of nodes) {
    for (const ref of node.context_refs) {
      const id = knowledgeRefId(ref);
      if (!id) continue;
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }
  return counts;
}
